// Live video for adults: go live with camera and microphone, watch, comment and send hearts.
// Media goes through LOUDERR's own media server and is not recorded. Comments and hearts arrive
// as data sent by the server after it checked them; this client never publishes data itself.
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.LouderrLive = api;
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  const DATA_TOPIC = "louderr.live";
  const SDK_URL = "/static/vendor/livekit-client-2.22.3.umd.js";
  const MAX_COMMENTS = 60;
  const MAX_HEARTS = 12;
  const REPORT_CATEGORIES = [
    ["sexual_content", "Nudity or sexual content"],
    ["hate_or_harassment", "Hate or harassment"],
    ["credible_threat", "Violence or threats"],
    ["self_harm", "Self-harm"],
    ["child_safety", "A child may be at risk"],
    ["spam_or_scam", "Spam or scam"],
    ["other", "Something else"],
  ];

  function decode(payload) {
    if (typeof payload === "string") return payload;
    if (typeof TextDecoder === "function") return new TextDecoder().decode(payload);
    return String.fromCharCode(...payload);
  }

  // Only the server's messages on our topic, in the shapes it sends; anything else is ignored.
  function parseData(payload, topic) {
    if (topic !== DATA_TOPIC || payload == null) return null;
    let message;
    try {
      const text = decode(payload);
      if (text.length > 12000) return null;
      message = JSON.parse(text);
    } catch (_) {
      return null;
    }
    if (!message || typeof message !== "object") return null;
    if (message.type === "heart") return { type: "heart" };
    if (message.type === "comment_removed" && typeof message.id === "string") {
      return { type: "comment_removed", id: message.id };
    }
    const comment = message.comment;
    if (
      message.type === "comment" && comment && typeof comment === "object"
      && typeof comment.id === "string" && typeof comment.body === "string"
    ) {
      return { type: "comment", comment };
    }
    return null;
  }

  function formatElapsed(ms) {
    const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = value => String(value).padStart(2, "0");
    return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
  }

  function viewerLabel(count) {
    const value = Math.max(0, Number(count) || 0);
    return value === 1 ? "1 watching" : `${value} watching`;
  }

  function create(hooks) {
    const doc = hooks.document || document;
    const state = {
      available: false, canHost: false, items: [], mine: null, owner: null,
      request: 0, timer: 0, session: null, watchRead: null, sdkPromise: null,
    };

    const node = (tag, className = "", text = "") => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text !== "") element.textContent = text;
      return element;
    };
    const svg = name => {
      const holder = node("span", "sv-icon");
      holder.innerHTML = `<svg aria-hidden="true"><use href="#icon-${name}"></use></svg>`;
      return holder;
    };
    const button = (label, className, handler, iconName = "", visible = "") => {
      const element = node("button", className);
      element.type = "button";
      element.setAttribute("aria-label", label);
      if (iconName) element.append(svg(iconName));
      if (visible) element.append(node("span", "", visible));
      else if (!iconName) element.textContent = label;
      element.addEventListener("click", event => { event.stopPropagation(); handler(event); });
      return element;
    };
    const say = (message, error = false) => hooks.toast?.(message, error);
    const viewerId = () => hooks.viewer()?.id || null;
    const uuid = () => (hooks.uuid ? hooks.uuid() : crypto.randomUUID());

    function avatar(user, className) {
      const element = node("span", `avatar ${className}`);
      hooks.styleAvatar?.(element, user);
      return element;
    }

    // A modal <dialog>, like the Stories viewer, so it stacks above everything else.
    function overlay(className, label) {
      const shell = node("dialog", `sv-overlay lv-overlay ${className}`);
      shell.setAttribute("aria-label", label);
      const previous = doc.activeElement;
      shell.addEventListener("cancel", event => {
        event.preventDefault();
        shell.dispatchEvent(new CustomEvent("lv-close"));
      });
      // Browser Back closes the native dialog directly. It must stop the session
      // through the same idempotent finish handler as Close and Escape.
      shell.addEventListener("close", () => shell.dispatchEvent(new CustomEvent("lv-close")));
      shell.dismiss = () => {
        if (shell.open && typeof shell.close === "function") shell.close();
        shell.remove();
        if (previous && typeof previous.focus === "function" && previous.isConnected) previous.focus();
      };
      doc.body.append(shell);
      if (typeof shell.showModal === "function") shell.showModal();
      else shell.setAttribute("open", "");
      return shell;
    }

    function sheet(title) {
      const shell = node("dialog", "sv-overlay sv-sheet-overlay");
      shell.setAttribute("aria-label", title);
      const panel = node("section", "sv-sheet");
      const header = node("header", "sv-sheet-header");
      const close = () => { if (shell.open) shell.close(); shell.remove(); };
      header.append(node("h2", "", title), button("Close", "sv-icon-button", close, "x"));
      const body = node("div", "sv-sheet-body");
      panel.append(header, body);
      shell.append(panel);
      shell.addEventListener("click", event => { if (event.target === shell) close(); });
      shell.addEventListener("cancel", event => { event.preventDefault(); close(); });
      doc.body.append(shell);
      if (typeof shell.showModal === "function") shell.showModal();
      else shell.setAttribute("open", "");
      return { shell, body, close };
    }

    // State ------------------------------------------------------------------------------------

    function schedule() {
      clearTimeout(state.timer);
      if (!viewerId()) return;
      const hidden = doc.visibilityState === "hidden";
      state.timer = setTimeout(() => { void refresh(); }, hidden ? 180000 : 60000);
      // Outside a browser (the tests) the timer must not keep the process alive.
      state.timer?.unref?.();
    }

    async function refresh() {
      const id = viewerId();
      if (!id || !hooks.capabilities?.()?.live) {
        const changed = state.available || state.items.length || state.mine;
        state.available = false; state.canHost = false; state.items = []; state.mine = null; state.owner = id;
        if (changed) hooks.onChanged?.();
        return state;
      }
      const request = ++state.request;
      try {
        const result = await hooks.api("/api/live/state");
        if (request !== state.request || viewerId() !== id) return state;
        state.available = result?.available === true;
        state.canHost = result?.can_host === true;
        state.items = Array.isArray(result?.items) ? result.items.filter(item => item?.id && item?.host?.id) : [];
        state.mine = result?.mine || null;
        state.owner = id;
      } catch (_) {
        if (request !== state.request) return state;
        state.items = [];
      }
      hooks.onChanged?.();
      schedule();
      return state;
    }

    function trayItems() {
      if (state.owner !== viewerId()) return [];
      return state.items.map(item => {
        const tile = node("button", "people-row-item has-story lv-tray-item");
        tile.type = "button";
        const face = avatar(item.host, "people-row-avatar");
        face.append(node("span", "lv-badge lv-tray-badge", "LIVE"));
        const name = item.host.display_name || item.host.handle;
        tile.append(face, node("span", "people-row-name", name));
        tile.setAttribute("aria-label", `${name} is live${item.title ? `: ${item.title}` : ""}`);
        tile.addEventListener("click", () => { void watch(item.id); });
        return tile;
      });
    }

    function liveFor(userId) {
      return state.owner === viewerId() ? state.items.find(item => item.host?.id === userId) || null : null;
    }

    function events(list) {
      if (Array.isArray(list) && list.some(event => event?.type === "live.started")) void refresh();
    }

    function cancelWatch() {
      const read = state.watchRead;
      state.watchRead = null;
      read?.controller.abort();
    }

    function syncIdentity() {
      if (state.watchRead && state.watchRead.owner !== viewerId()) cancelWatch();
      if (state.session && state.session.owner !== viewerId()) void state.session.close({ silent: true });
      if (state.owner && state.owner !== viewerId()) {
        state.items = []; state.mine = null; state.available = false; state.canHost = false;
        state.owner = null;
        hooks.onChanged?.();
      }
    }

    // Media ------------------------------------------------------------------------------------

    function sdk() {
      if (globalThis.LivekitClient) return Promise.resolve(globalThis.LivekitClient);
      if (!state.sdkPromise) {
        state.sdkPromise = new Promise((resolve, reject) => {
          const script = node("script");
          script.src = SDK_URL;
          script.onload = () => (globalThis.LivekitClient ? resolve(globalThis.LivekitClient) : reject(new Error("Live video could not be loaded.")));
          script.onerror = () => { script.remove(); state.sdkPromise = null; reject(new Error("Live video could not be loaded.")); };
          doc.head.append(script);
        });
      }
      return state.sdkPromise;
    }

    // A shared frame for host and viewer: the video stage, a top bar, comments and a bottom bar.
    function frame(label) {
      const shell = overlay("lv-live", label);
      const stage = node("div", "lv-stage");
      const video = node("div", "lv-video");
      const hearts = node("div", "lv-hearts");
      hearts.setAttribute("aria-hidden", "true");
      const top = node("header", "lv-top");
      const status = node("div", "lv-status");
      const badge = node("span", "lv-badge", "LIVE");
      const clock = node("span", "lv-clock");
      const count = node("span", "lv-count");
      status.append(badge, clock, count);
      const comments = node("ol", "lv-comments");
      comments.setAttribute("aria-live", "polite");
      comments.setAttribute("aria-label", "Comments");
      const bottom = node("footer", "lv-bottom");
      const notice = node("p", "lv-notice");
      notice.setAttribute("role", "status");
      stage.append(video, hearts, top, comments, notice, bottom);
      shell.append(stage);
      return { shell, stage, video, hearts, top, status, badge, clock, count, comments, bottom, notice };
    }

    function floatHeart(parts) {
      if (parts.hearts.childElementCount >= MAX_HEARTS) return;
      const heart = node("span", "lv-heart", "♥");
      heart.style.setProperty("--lv-drift", `${Math.round(Math.random() * 40 - 20)}px`);
      parts.hearts.append(heart);
      setTimeout(() => heart.remove(), 1600);
    }

    function commentRow(comment, { canRemove, onRemove }) {
      const row = node("li", "lv-comment");
      row.dataset.commentId = comment.id;
      const face = avatar(comment.author || {}, "lv-comment-avatar");
      const text = node("p", "lv-comment-text");
      const name = node("strong", "", (comment.author?.display_name || comment.author?.handle || "Member") + " ");
      text.append(name, doc.createTextNode(comment.body));
      row.append(face, text);
      if (canRemove) {
        row.append(button("Remove comment", "sv-icon-button lv-comment-remove", () => onRemove(comment), "x"));
      }
      return row;
    }

    function addComment(parts, comment, options) {
      if (!comment?.id || parts.comments.querySelector(`[data-comment-id="${CSS.escape(comment.id)}"]`)) return;
      parts.comments.append(commentRow(comment, options(comment)));
      while (parts.comments.childElementCount > MAX_COMMENTS) parts.comments.firstElementChild.remove();
      parts.comments.scrollTop = parts.comments.scrollHeight;
    }

    function removeComment(parts, id) {
      parts.comments.querySelector(`[data-comment-id="${CSS.escape(id)}"]`)?.remove();
    }

    function composer(parts, liveId, commentOptions) {
      const form = node("form", "lv-compose");
      const input = node("input", "lv-compose-input");
      input.type = "text";
      input.maxLength = 300;
      input.placeholder = "Add a comment";
      input.setAttribute("aria-label", "Add a comment");
      input.autocomplete = "off";
      const send = node("button", "lv-compose-send", "Send");
      send.type = "submit";
      let token = uuid();
      form.addEventListener("submit", async event => {
        event.preventDefault();
        const body = input.value.trim();
        if (!body || send.disabled) return;
        send.disabled = true;
        try {
          const result = await hooks.api(`/api/live/${encodeURIComponent(liveId)}/comments`, {
            method: "POST", csrf: true, json: { comment_token: token, body },
          });
          addComment(parts, result.comment, commentOptions);
          input.value = "";
          token = uuid();
        } catch (error) {
          say(error?.message || "The comment was not posted.", true);
        } finally {
          send.disabled = false;
        }
      });
      form.append(input, send);
      return form;
    }

    function startClock(parts, startedAt) {
      const started = startedAt ? Date.parse(startedAt) : Date.now();
      const tick = () => { parts.clock.textContent = formatElapsed(Date.now() - started); };
      tick();
      return setInterval(tick, 1000);
    }

    async function reportLive(liveId) {
      const picker = sheet("Report this live");
      picker.body.append(node("p", "sv-hint", "Reports are private. The host is not told who reported. A moderator can end a live at once."));
      for (const [value, label] of REPORT_CATEGORIES) {
        picker.body.append(button(label, "sv-choice", async () => {
          try {
            await hooks.api("/api/safety/reports", {
              method: "POST", csrf: true,
              json: { target_type: "live_stream", target_id: liveId, category: value },
            });
            say("Thanks. Our team will review this live.");
          } catch (error) {
            say(error?.message || "The report was not sent.", true);
          }
          picker.close();
        }, "", label));
      }
    }

    // Hosting ----------------------------------------------------------------------------------

    async function openHost() {
      if (!state.canHost) { say("Going live is for verified adults.", true); return; }
      if (state.session) return;
      cancelWatch();
      const parts = frame("Go live");
      parts.stage.classList.add("is-host", "is-preview");
      parts.badge.hidden = true;
      let lib = null, room = null, tracks = [], live = null, connection = null;
      let closed = false, clock = 0, poll = 0, facing = "user";
      const session = { owner: viewerId(), close: finish };
      state.session = session;

      const title = node("input", "lv-title-input");
      title.type = "text";
      title.maxLength = 80;
      title.placeholder = "Add a title (optional)";
      title.setAttribute("aria-label", "Live title");
      const hint = node("p", "lv-hint", "Adults who may see your profile can watch, and your followers are told. Live video is not recorded.");
      const start = node("button", "lv-primary", "Go live");
      start.type = "button";
      const flip = button("Switch camera", "sv-round lv-round", () => { void switchCamera(); }, "refresh");
      const mute = button("Mute microphone", "lv-pill", () => { void toggleMicrophone(); }, "", "Mute");
      const close = button("Close", "sv-icon-button lv-close", () => { void finish(); }, "x");
      parts.top.append(parts.status, close);
      parts.bottom.append(title, hint, start);
      parts.shell.addEventListener("lv-close", () => { void finish(); });

      async function switchCamera() {
        const camera = tracks.find(track => track.kind === "video");
        if (!camera || closed) return;
        facing = facing === "user" ? "environment" : "user";
        try { await camera.restartTrack({ facingMode: facing }); }
        catch (_) { if (!closed) say("The camera could not be switched.", true); }
        finally { if (closed) camera.stop(); }
      }

      async function toggleMicrophone() {
        const microphone = tracks.find(track => track.kind === "audio");
        if (!microphone || closed) return;
        try {
          if (microphone.isMuted) await microphone.unmute(); else await microphone.mute();
        } catch (_) {
          if (!closed) say("The microphone could not be changed.", true);
          return;
        } finally { if (closed) microphone.stop(); }
        if (closed) return;
        mute.setAttribute("aria-label", microphone.isMuted ? "Unmute microphone" : "Mute microphone");
        mute.querySelector("span").textContent = microphone.isMuted ? "Unmute" : "Mute";
        mute.classList.toggle("is-off", microphone.isMuted);
      }

      function onData(payload, participant, kind, topic) {
        if (participant) return;
        const message = parseData(payload, topic);
        if (!message) return;
        if (message.type === "heart") floatHeart(parts);
        else if (message.type === "comment") addComment(parts, message.comment, hostComment);
        else if (message.type === "comment_removed") removeComment(parts, message.id);
      }

      function hostComment() {
        return {
          canRemove: true,
          onRemove: async comment => {
            try {
              await hooks.api(`/api/live/${encodeURIComponent(live.id)}/comments/${encodeURIComponent(comment.id)}`, {
                method: "DELETE", csrf: true,
              });
              removeComment(parts, comment.id);
            } catch (error) {
              say(error?.message || "The comment was not removed.", true);
            }
          },
        };
      }

      async function pollState() {
        if (!live || closed) return;
        try {
          const detail = await hooks.api(`/api/live/${encodeURIComponent(live.id)}`);
          if (closed) return;
          parts.count.textContent = viewerLabel(detail.viewers);
          if (detail.state !== "live") {
            parts.notice.textContent = detail.end_reason === "staff"
              ? "A moderator ended your live."
              : "Your live has ended.";
            await finish({ ended: true, keepNotice: true });
          }
        } catch (_) { /* The next poll tries again. */ }
      }

      async function goLive() {
        if (live || closed) return;
        start.disabled = true;
        title.disabled = true;
        parts.notice.textContent = "Starting…";
        try {
          const created = await hooks.api("/api/live", {
            method: "POST", csrf: true, json: { create_token: uuid(), title: title.value.trim() },
          });
          live = created.live;
          // Closed while the live was being created: end it at once.
          if (closed) { await endOnServer(); return; }
          connection = await hooks.api(`/api/live/${encodeURIComponent(live.id)}/connection`, {
            method: "POST", csrf: true, json: { send_token: uuid() },
          });
          if (closed) {
            hooks.api(`/api/live/connections/${encodeURIComponent(connection.connection_id)}/close`, { method: "POST", csrf: true }).catch(() => {});
            return;
          }
          room = new lib.Room({ adaptiveStream: false, dynacast: true });
          room.on(lib.RoomEvent.DataReceived, onData);
          room.on(lib.RoomEvent.Disconnected, () => {
            if (!closed) parts.notice.textContent = "Connection lost. End the live and start again if it does not come back.";
          });
          room.on(lib.RoomEvent.Reconnecting, () => { if (!closed) parts.notice.textContent = "Reconnecting…"; });
          room.on(lib.RoomEvent.Reconnected, () => { if (!closed) parts.notice.textContent = ""; });
          await room.connect(connection.server_url, connection.token, { rtcConfig: { iceServers: [] } });
          if (closed) return;
          for (const track of tracks) {
            if (closed) return;
            await room.localParticipant.publishTrack(track);
          }
          if (closed) return;
          parts.stage.classList.remove("is-preview");
          parts.badge.hidden = false;
          parts.notice.textContent = "";
          title.remove(); hint.remove(); start.remove();
          const end = node("button", "lv-end", "End live");
          end.type = "button";
          end.addEventListener("click", () => { void confirmEnd(); });
          parts.bottom.append(flip, mute, end);
          parts.count.textContent = viewerLabel(0);
          clock = startClock(parts, new Date().toISOString());
          poll = setInterval(() => { void pollState(); }, 5000);
          state.mine = live;
          hooks.onChanged?.();
        } catch (error) {
          if (closed) return;
          parts.notice.textContent = "";
          say(error?.message || "The live could not start.", true);
          await finish();
        }
      }

      async function confirmEnd() {
        const ok = hooks.confirm ? await hooks.confirm("End your live for everyone?", { confirmLabel: "End live" }) : true;
        if (ok) await finish();
      }

      async function endOnServer() {
        if (!live) return;
        try {
          await hooks.api(`/api/live/${encodeURIComponent(live.id)}/end`, { method: "POST", csrf: true });
        } catch (_) { /* The server also ends a live whose host is gone. */ }
      }

      async function finish(options = {}) {
        if (closed) return;
        closed = true;
        clearInterval(clock); clearInterval(poll);
        // Release local capture and the visible session immediately. Neither the
        // server's end reply nor the media transport may keep a closed camera on.
        for (const track of tracks) track.stop();
        tracks = [];
        const activeRoom = room;
        room = null;
        activeRoom?.removeAllListeners?.();
        const disconnected = activeRoom?.disconnect(true).catch(() => {});
        const ended = live && !options.ended ? endOnServer() : null;
        if (connection) {
          hooks.api(`/api/live/connections/${encodeURIComponent(connection.connection_id)}/close`, { method: "POST", csrf: true }).catch(() => {});
        }
        if (state.session === session) state.session = null;
        state.mine = null;
        hooks.onChanged?.();
        if (options.keepNotice && !options.silent) {
          setTimeout(() => parts.shell.dismiss(), 2500);
        } else {
          parts.shell.dismiss();
        }
        await Promise.all([disconnected, ended]);
        if (live && !options.silent && !options.keepNotice) say("Your live ended.");
        void refresh();
      }

      start.addEventListener("click", () => { void goLive(); });
      parts.notice.textContent = "Starting the camera…";
      try {
        lib = await sdk();
        if (closed) return;
        tracks = await lib.createLocalTracks({ audio: true, video: { facingMode: facing, resolution: lib.VideoPresets?.h720?.resolution } });
        if (closed) { tracks.forEach(track => track.stop()); tracks = []; return; }
        const camera = tracks.find(track => track.kind === "video");
        if (camera) {
          const element = camera.attach();
          element.muted = true;
          element.playsInline = true;
          element.classList.add("is-mirrored");
          parts.video.append(element);
        }
        parts.notice.textContent = "";
        title.focus();
      } catch (error) {
        if (closed) return;
        parts.notice.textContent = "";
        say(error?.name === "NotAllowedError" ? "Camera or microphone permission was not granted." : (error?.message || "The camera could not start."), true);
        await finish({ silent: true });
      }
    }

    // Watching ---------------------------------------------------------------------------------

    async function watch(liveId) {
      if (!liveId || state.session) return;
      cancelWatch();
      const owner = viewerId(), navigation = hooks.watchOwner?.() || [];
      const read = { owner, controller: new AbortController() };
      state.watchRead = read;
      const current = () => {
        const next = hooks.watchOwner?.() || [];
        return state.watchRead === read && !read.controller.signal.aborted && owner === viewerId()
          && !state.session && navigation.length === next.length && navigation.every((value, index) => value === next[index]);
      };
      let detail;
      try {
        detail = await hooks.api(`/api/live/${encodeURIComponent(liveId)}`, { signal: read.controller.signal });
        if (!current()) return;
      } catch (error) {
        if (!current()) return;
        say(error?.status === 404 ? "This live is not available." : (error?.message || "The live could not open."), true);
        void refresh();
        return;
      } finally {
        if (state.watchRead === read) state.watchRead = null;
      }
      if (detail.is_host) { say("This is your own live."); return; }
      if (detail.state !== "live") { say("This live has ended."); void refresh(); return; }
      const parts = frame(`Live from ${detail.host?.display_name || detail.host?.handle || "a member"}`);
      let room = null, connection = null, closed = false, clock = 0, poll = 0, heartBusy = false;
      const session = { owner, close: finish };
      state.session = session;

      const identity = node("button", "lv-identity");
      identity.type = "button";
      identity.append(avatar(detail.host, "lv-identity-avatar"));
      const names = node("span", "lv-identity-text");
      names.append(node("strong", "", detail.host?.display_name || detail.host?.handle || "Member"));
      if (detail.title) names.append(node("small", "", detail.title));
      identity.append(names);
      identity.addEventListener("click", () => {
        if (detail.host?.handle) { void finish(); hooks.openProfile?.(detail.host.handle); }
      });
      const report = button("Report live", "sv-icon-button lv-report", () => { void reportLive(liveId); }, "flag");
      const close = button("Close", "sv-icon-button lv-close", () => { void finish(); }, "x");
      parts.top.append(identity, parts.status, report, close);
      const sound = node("button", "lv-sound", "Tap for sound");
      sound.type = "button";
      sound.hidden = true;
      sound.addEventListener("click", async () => {
        try { await room?.startAudio(); sound.hidden = true; } catch (_) { say("Sound could not start.", true); }
      });
      const viewerComment = comment => ({
        canRemove: comment.author?.id === viewerId(),
        onRemove: async item => {
          try {
            await hooks.api(`/api/live/${encodeURIComponent(liveId)}/comments/${encodeURIComponent(item.id)}`, {
              method: "DELETE", csrf: true,
            });
            removeComment(parts, item.id);
          } catch (error) {
            say(error?.message || "The comment was not removed.", true);
          }
        },
      });
      const heart = button("Send a heart", "sv-round lv-round lv-heart-button", async () => {
        if (heartBusy) return;
        heartBusy = true;
        floatHeart(parts);
        try {
          await hooks.api(`/api/live/${encodeURIComponent(liveId)}/hearts`, { method: "POST", csrf: true });
        } catch (_) { /* Hearts are best effort. */ }
        setTimeout(() => { heartBusy = false; }, 400);
      }, "heart");
      parts.bottom.append(composer(parts, liveId, viewerComment), heart);
      parts.stage.append(sound);
      parts.shell.addEventListener("lv-close", () => { void finish(); });
      for (const comment of detail.comments || []) addComment(parts, comment, viewerComment);
      parts.count.textContent = viewerLabel(detail.viewers);
      clock = startClock(parts, detail.started_at);
      parts.notice.textContent = "Joining…";

      async function pollState() {
        if (closed) return;
        try {
          const next = await hooks.api(`/api/live/${encodeURIComponent(liveId)}`);
          if (closed) return;
          parts.count.textContent = viewerLabel(next.viewers);
          if (next.state !== "live") ended();
        } catch (error) {
          if (error?.status === 404) ended();
        }
      }

      function ended(message = "This live has ended.") {
        if (closed) return;
        parts.notice.textContent = message;
        parts.stage.classList.add("is-ended");
        void finish({ keepNotice: true });
      }

      async function finish(options = {}) {
        if (closed) return;
        closed = true;
        clearInterval(clock); clearInterval(poll);
        if (connection) {
          hooks.api(`/api/live/connections/${encodeURIComponent(connection.connection_id)}/close`, { method: "POST", csrf: true }).catch(() => {});
        }
        const activeRoom = room;
        room = null;
        activeRoom?.removeAllListeners?.();
        const disconnected = activeRoom?.disconnect(true).catch(() => {});
        if (state.session === session) state.session = null;
        if (options.keepNotice && !options.silent) setTimeout(() => parts.shell.dismiss(), 2500);
        else parts.shell.dismiss();
        await disconnected;
        void refresh();
      }

      try {
        const lib = await sdk();
        if (closed) return;
        connection = await hooks.api(`/api/live/${encodeURIComponent(liveId)}/connection`, {
          method: "POST", csrf: true, json: { send_token: uuid() },
        });
        if (closed) {
          hooks.api(`/api/live/connections/${encodeURIComponent(connection.connection_id)}/close`, { method: "POST", csrf: true }).catch(() => {});
          return;
        }
        room = new lib.Room({ adaptiveStream: true, dynacast: true });
        room.on(lib.RoomEvent.TrackSubscribed, track => {
          if (closed) return;
          const element = track.attach();
          element.playsInline = true;
          element.autoplay = true;
          if (track.kind === "video") parts.video.replaceChildren(element);
          else { element.hidden = true; parts.stage.append(element); }
        });
        room.on(lib.RoomEvent.TrackUnsubscribed, track => { track.detach().forEach(element => element.remove()); });
        room.on(lib.RoomEvent.DataReceived, (payload, participant, kind, topic) => {
          if (participant) return;
          const message = parseData(payload, topic);
          if (!message) return;
          if (message.type === "heart") floatHeart(parts);
          else if (message.type === "comment") addComment(parts, message.comment, viewerComment);
          else if (message.type === "comment_removed") removeComment(parts, message.id);
        });
        room.on(lib.RoomEvent.AudioPlaybackStatusChanged, () => { sound.hidden = Boolean(room?.canPlaybackAudio); });
        // Still live after the check: the connection went (a lost network, or this live opened elsewhere,
        // which replaces this connection), not the live.
        room.on(lib.RoomEvent.Disconnected, () => { if (!closed) void pollState().then(() => { if (!closed) ended("You were disconnected from this live."); }); });
        await room.connect(connection.server_url, connection.token, { rtcConfig: { iceServers: [] } });
        if (closed) return;
        try { await room.startAudio(); } catch (_) { /* The button below asks for a tap. */ }
        if (closed) return;
        sound.hidden = Boolean(room.canPlaybackAudio);
        parts.notice.textContent = "";
        void pollState();
        poll = setInterval(() => { void pollState(); }, 5000);
      } catch (error) {
        if (closed) return;
        parts.notice.textContent = "";
        say(error?.status === 429 ? (error.message || "This live is full right now.") : (error?.message || "The live could not open."), true);
        await finish({ silent: true });
      }
    }

    doc.addEventListener?.("visibilitychange", () => {
      if (doc.visibilityState === "visible" && viewerId()) void refresh();
    });

    function liveCount() {
      return state.owner === viewerId() ? state.items.length : 0;
    }

    return { refresh, trayItems, liveCount, liveFor, events, syncIdentity, cancelWatch, openHost, watch,
      canHost: () => state.canHost && !state.mine, available: () => state.available };
  }

  return { create, _test: { parseData, formatElapsed, viewerLabel, DATA_TOPIC } };
});
